// The sales process as rows: pipelines, stages, picklists and custom field
// definitions (GET /api/config), and the capability flags that decide whether
// the web app may offer anything built on them.
//
// The admin Pipeline tab renders only when `v2Writes` is true. Until the API
// writes the new tables, a second deal or a custom value has no old row to be
// copied back into, so the API refuses those writes and the UI hides them.
// The deal writes also wait for the phase7 setting: the Deals section and
// the new-pipeline control render only when `dealWrites` says this viewer
// may open, change and close deals.
import { useQuery } from '@tanstack/react-query'
import { apiFetch } from './core'

export interface Capabilities {
  v2Writes: boolean
  /** off: nobody writes deals; admins: holders of manage_pipeline; everyone. */
  phase7: 'off' | 'admins' | 'everyone'
  /** Whether this viewer may open, change and close deals now. */
  dealWrites: boolean
}

export function useCapabilities() {
  return useQuery({
    queryKey: ['capabilities'],
    queryFn: () => apiFetch<Capabilities>('/api/config/capabilities'),
    staleTime: 5 * 60_000,
  })
}

/** True only once the API has said so. Loading and errors read as false. */
export function useV2Writes(): boolean {
  return useCapabilities().data?.v2Writes === true
}

/** True only once the API has said this viewer may write deals. */
export function useDealWrites(): boolean {
  return useCapabilities().data?.dealWrites === true
}

export type StageCategory = 'open' | 'won' | 'lost'

export interface StageDef {
  id: string
  pipelineId: string
  label: string
  position: number
  category: StageCategory
  inQueue: boolean
  color: string | null
  archivedAt: string | null
  /** Every deal ever in this stage, and the open ones. */
  deals: number
  openDeals: number
  /** A seeded stage of the default pipeline: its label is fixed, and
   *  deleting it archives it, while stages are still found by label. */
  system: boolean
}

export interface PipelineDef {
  id: string
  name: string
  position: number
  isDefault: boolean
  stages: StageDef[]
}

/** What choosing an option does to a deal (call outcomes). */
export interface OptionEffects {
  stage?: string
  made_contact?: boolean
  terminal?: boolean
  activity_type?: string
  next_step_days?: number
}

export interface PicklistOptionDef {
  id: string
  picklistId: string
  value: string
  label: string
  position: number
  color: string | null
  effects: OptionEffects
  archivedAt: string | null
}

export interface PicklistDef {
  id: string
  key: string
  label: string
  /** Seeded with the schema (call_outcome, meeting_outcome, lost_reason). */
  system: boolean
  options: PicklistOptionDef[]
}

export const FIELD_ENTITIES = ['deal', 'company', 'contact', 'activity'] as const
export type FieldEntity = (typeof FIELD_ENTITIES)[number]

export const FIELD_TYPES = ['text', 'number', 'date', 'bool', 'select', 'multi_select', 'user', 'url'] as const
export type FieldType = (typeof FIELD_TYPES)[number]

export interface FieldDef {
  id: string
  entity: FieldEntity
  key: string
  label: string
  type: FieldType
  picklistId: string | null
  required: boolean
  showInTable: boolean
  position: number
  archivedAt: string | null
  system: boolean
}

export interface ProcessConfig {
  pipelines: PipelineDef[]
  picklists: PicklistDef[]
  fields: FieldDef[]
}

export function useProcessConfig(enabled = true) {
  return useQuery({
    queryKey: ['process-config'],
    queryFn: () => apiFetch<ProcessConfig>('/api/config'),
    enabled,
    staleTime: 60_000,
  })
}

/** A person by users.id, for `user` custom fields. */
export interface PersonRef {
  id: string
  email: string
  name: string
}

export function useConfigUsers(enabled = true) {
  return useQuery({
    queryKey: ['process-config', 'users'],
    queryFn: () => apiFetch<{ users: PersonRef[] }>('/api/config/users'),
    enabled,
    staleTime: 10 * 60_000,
  })
}
